import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { catchError, concatMap, first, map } from 'rxjs/operators';
import * as _ from 'underscore';
import { Enum, KeyPair } from '../../../models/base';
import { CompanyDeductionConfig } from '../../../models/company-deduction-config';
import { CompanyEarningConfig } from '../../../models/company-earning-config';
import {
  AddPaylineRequestModel,
  EmployeePay,
  MasterPayrollObject,
  OpenPaylineDetailsArgs,
  Payline,
  PaylineType,
  PayrollStartData,
  PayTypes
} from '../../../models/payroll';
import { CompanyTaxCodeConfig } from '../../../models/tax-config';
import { AuditService } from '../../../services/audit.service';
import { CompaniesService } from '../../../services/companies.service';
import { EnumService } from '../../../services/enum.service';
import { ModalService } from '../../../services/modal.service';
import { PayrollService } from '../../../services/payroll.service';
import { PeopleService } from '../../../services/people.service';
import { PayrollManagement } from '../../../tools/payroll-management';
import { ModalStates } from '../../../models/modal';
import { downloadFile } from '../../../tools/reports-tools';
import { ReportsService } from '../../../services/reports.service';
import { Store } from '@ngxs/store';
import {
  ClientSelectionLoadOpenChangeRequests,
  ClientSelectionState
} from '../../../state/ClientSelectionState/ClientSelection.state';
import { ChangeRequestHistoryEntry } from '../../../models/change-requests';
import { WarningLevel } from '../../../models/error-detail';
import { InstanceTypes } from '../../../models/instance-types';
import { BasePayrollComponent } from '../../../components/base/base-payroll.component';
import { CheckboxSizes } from '../../../components/checkbox/checkbox.component';

@Component({
  selector: 'lib-manual-payroll',
  templateUrl: './manual-payroll.component.html',
  styleUrls: ['./manual-payroll.component.scss'],
  standalone: false
})
export class ManualPayrollComponent extends BasePayrollComponent {
  public data: KeyPair[] = [];
  public employeeStatuses: Enum[];
  public keyword = 'Value';
  public query: string;
  public paylineDetailArgs: OpenPaylineDetailsArgs;
  public manualPaylines: SelectableManualPayline[];
  public currentPage: SelectableManualPayline[];
  public payroll: MasterPayrollObject;
  public searchValue: string;
  public selEmployeePay: EmployeePay;
  public showPaylineDetails: boolean;
  public startData: PayrollStartData;
  public selectedLines: number[] = [];
  public selectAll: boolean = false;

  public checkSizes = CheckboxSizes;
  // configs
  public deductionConfigs: Record<string, CompanyDeductionConfig> = {};
  public earningConfigs: Record<string, CompanyEarningConfig> = {};
  public taxConfigs: Record<string, CompanyTaxCodeConfig> = {};

  public changeRequests: ChangeRequestHistoryEntry[] = [];
  public showRequestWarning: boolean = false;

  get baseUrl() {
    if (this.env.instanceType == InstanceTypes.CsrPortal) {
      return '/client';
    }
    return '';
  }

  constructor(
    private companyService: CompaniesService,
    private enumService: EnumService,
    private ngModalService: NgbModal,
    private modalService: ModalService,
    private payrollMgmt: PayrollManagement,
    private payrollService: PayrollService,
    private peopleService: PeopleService,
    private router: Router,
    private auditService: AuditService,
    private reportService: ReportsService,
    private store: Store,
    @Inject('env') private env: any
  ) {
    super();
    this._searchPerson = _.debounce(this._searchPerson, 250);
  }

  ngOnInit(): void {
    this.currentPage = [];
    // fetch payroll from cache
    this.payrollMgmt
      .getManualPayroll(this.clientCode, this.year, this.seqNo)
      .pipe(
        concatMap(p => {
          this.payroll = p;

          // error condition - payroll type doesn't match up.
          if (this.payroll.PayType != PayTypes.Manual) {
            this.navigateToPayrollHub();
          }
          this.store.dispatch(new ClientSelectionLoadOpenChangeRequests()).subscribe(_ => {
            this.store
              .selectOnce(ClientSelectionState.getOpenChangeRequests)
              .subscribe(requests => {
                this.changeRequests = requests;

                this._updateScreen();
              });
          });

          return this.payrollService
            .getPayrollConfigSetupData(
              p.ClientCode,
              p.Calendars,
              p.LocationId,
              p.PayDate,
              p.PayType
            )
            .pipe(
              map(setupData => {
                setupData.Deductions.forEach(config => {
                  this.deductionConfigs[config.Id] = config;
                });

                setupData.Earnings.forEach(config => {
                  this.earningConfigs[config.Id] = config;
                });
              })
            );
        })
      )
      .subscribe();

    this.payrollService
      .getPayrollStartData(this.clientCode)
      .pipe(map(data => (this.startData = data)))
      .subscribe();

    this.companyService
      .getTaxConfigs(this.clientCode, false)
      .pipe(
        map(taxConfigs => {
          for (let config of taxConfigs) {
            this.taxConfigs[config.TaxCode] = config;
          }
        })
      )
      .subscribe();

    this.enumService
      .get('EmployeeStatuses')
      .pipe(map(data => (this.employeeStatuses = data)))
      .subscribe();
  }
  public checkChanged(val: any, stub: SelectableManualPayline) {
    stub.Selected = val;
    if (val) {
      this.selectedLines.push(stub.Payline.PaylineId);
    } else {
      this.selectedLines = this.selectedLines.filter(x => x != stub.Payline.PaylineId);
    }
  }
  public closePaylineDetails(details: { pay: EmployeePay; line: Payline; isDirty: boolean }) {
    // the payline detail editor has been closed from its component

    // if no data is passed back, user cancelled out of the payline detail editor
    if (!details) {
      this.showPaylineDetails = false;
      this._updateScreen();
      return;
    }

    // update payroll model with the edited values from the detail editor
    const index = this.payroll.EmployeePays.findIndex(x => x.PersonId == details.pay.PersonId);
    this.payroll.EmployeePays[index] = details.pay;

    this.selEmployeePay = null;
    this.showPaylineDetails = false;

    this.payrollMgmt.updateEmployeePay(
      this.clientCode,
      this.year,
      this.seqNo,
      this.payroll.EmployeePays[index]
    );

    this.auditService.auditPayroll(
      'payroll-manual-check-saved',
      {
        ClientCode: this.clientCode,
        LocationId: this.payroll.LocationId,
        PayDate: this.payroll.PayDate,
        PayrollType: PayTypes.Manual,
        Calendars: this.payroll.Calendars
      },
      {
        PersonId: details.pay.EmployeeNumber,
        PersonFullName: this.payrollMgmt.getEmployeeFullName(details.pay),
        PaylineId: details.line.PaylineId,
        NetCheckAmount: details.line.NetCheckAmountPaid ?? 0,
        CheckNumber: details.line.OverrideCheckNumber
      }
    );

    this._updateScreen();
  }
  public pageChanged(currentPage: SelectableManualPayline[]) {
    this.currentPage = currentPage;
    this.selectAll = this.currentPage.every(x => x.Selected);
  }
  public delete(m: ManualPayline) {
    this.modalService.showConfirmModal(
      'Are you sure you want to remove this entry? This action is permanent and cannot be reversed.',
      'Cancel',
      'Remove Entry',
      `Remove ${m.Name}`
    );
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (!confirm) {
        return;
      }

      // fire calc payline request
      const pay = this.payroll.EmployeePays.find(x => x.PersonId == m.PersonId);
      const line = pay.Paylines.find(x => x.PaylineId == m.Payline.PaylineId);
      line.OkToPay = false;

      this.payrollService
        .calculatePaylines(this.clientCode, m.PersonId, this.year, this.seqNo, pay.Paylines)
        .pipe(
          catchError(err => {
            console.log(err);
            // calculate payline will return 400 if any
            // have have critical errors. ensure this doesn't cancel
            // the remainder of the requests
            return of(err);
          })
        )
        .pipe(
          map((response: Payline[]) => {
            if (!response || !response.length) {
              return null;
            }

            const index = this.payroll.EmployeePays.indexOf(pay);
            this.payroll.EmployeePays[index].Paylines = response;

            this.auditService.auditPayroll(
              'payroll-manual-check-removed',
              {
                ClientCode: this.clientCode,
                LocationId: this.payroll.LocationId,
                PayDate: this.payroll.PayDate,
                PayrollType: PayTypes.Manual,
                Calendars: this.payroll.Calendars
              },
              {
                PersonId: pay.EmployeeNumber,
                PersonFullName: this.payrollMgmt.getEmployeeFullName(pay),
                PaylineId: line.PaylineId,
                NetCheckAmount: line.NetCheckAmountPaid,
                CheckNumber: line.OverrideCheckNumber
              }
            );

            this.payrollMgmt.updateEmployeePay(
              this.clientCode,
              this.year,
              this.seqNo,
              this.payroll.EmployeePays[index]
            );
          })
        )
        .subscribe(() => this._updateScreen());
    });
  }

  public reportGenerate() {
    const paylines = this.manualPaylines.filter(x =>
      this.selectedLines.includes(x.Payline.PaylineId)
    );
    if (paylines?.some(x => x.Payline.CanEdit)) {
      //we have paylines that aren't saved yet.
      const observableOrBoolean = this.payrollMgmt.onManualPayrollDeactivate(this.payroll);
      if (typeof observableOrBoolean != 'boolean') {
        observableOrBoolean.subscribe(_ => {
          this._reportGenerate();
          this.payrollMgmt.startManualPay(this.clientCode, this.payroll.Year, this.startData);
          paylines.forEach(x => {
            x.Payline.CanEdit = false;
          });
        });
      }
    } else {
      this._reportGenerate();
    }
  }

  public onChangeSearch($event) {
    this.query = $event;

    if (!$event || !$event.trim() || $event.trim().length < 2) {
      this.data = [];
    } else {
      this._searchPerson($event.trim());
    }
  }

  public open(m: ManualPayline) {
    const pay = this.payroll.EmployeePays.find(x => x.PersonId == m.PersonId);
    this.selEmployeePay = JSON.parse(JSON.stringify(pay));

    this.paylineDetailArgs = new OpenPaylineDetailsArgs(
      null,
      m.Payline.PaylineId,
      m.PersonId,
      null,
      true,
      false,
      !m.Payline.CanEdit
    );

    this.showPaylineDetails = true;
  }

  public navigateToPayrollHub() {
    this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
  }

  public navigateToPreview() {
    this.router.navigate([
      `/client/${this.clientCode}/manual-payroll-preview/${this.year}/${this.seqNo}`
    ]);
  }

  public selectEvent(person: { Id: number; Value: string }) {
    // user has picked a person
    const pay = this.payroll.EmployeePays.find(x => x.PersonId == person.Id);
    if (pay) {
      // person exists on this payroll
      // fire 'add person' endpoint
      this.payrollService
        .addNewPayline(
          this.clientCode,
          this.year,
          this.seqNo,
          person.Id,
          new AddPaylineRequestModel(PaylineType.Normal)
        )
        .subscribe(resp => {
          // update in memory and cached copies of payroll
          if (!resp.Model) {
            return;
          }

          pay.Paylines = resp.Model;
          pay.Paylines.forEach(line => {
            if (this.changeRequests?.some(x => x.PersonId == pay.PersonId)) {
              //This EE has change requests.
              this.showRequestWarning = true;
              if (line.Errors == null) {
                line.Errors = [];
              }
              this.changeRequests
                .filter(x => x.PersonId == pay.PersonId && x.RequestType == 'Address Change')
                .forEach(request => {
                  line.Errors.push({
                    FieldName: 'changeRequest',
                    Message: `This person has a pending address change request`,
                    WarningLevel: WarningLevel.Warning
                  });
                });
            }
          });
          this._openCalcPayModal(person.Id, pay.Paylines[pay.Paylines.length - 1].PaylineId);
        });
    } else {
      // person does not yet exist on this payroll
      // fire 'add person'
      this.payrollService
        .addPerson(this.clientCode, this.year, this.seqNo, person.Id)
        .subscribe(resp => {
          if (!resp.Model) {
            return;
          }
          resp.Model.Paylines.forEach(line => {
            if (this.changeRequests?.some(x => x.PersonId == resp.Model.PersonId)) {
              //This EE has change requests.
              this.showRequestWarning = true;
              if (line.Errors == null) {
                line.Errors = [];
              }
              this.changeRequests
                .filter(x => x.PersonId == resp.Model.PersonId && x.RequestType == 'Address Change')
                .forEach(request => {
                  line.Errors.push({
                    FieldName: 'changeRequest',
                    Message: `This person has a pending address change request`,
                    WarningLevel: WarningLevel.Warning
                  });
                });
            }
          });
          // update in memory and cached copies of payroll
          this.payroll.EmployeePays.push(resp.Model);

          this._openCalcPayModal(person.Id, resp.Model.Paylines[0].PaylineId);
        });
    }
  }
  public selectAllChanged(val: boolean) {
    this.selectAll = val;
    this.manualPaylines.forEach(stub => {
      if (this.currentPage.includes(stub)) {
        this.checkChanged(val, stub);
      }
    });
  }
  private _createManualPayline(pay: EmployeePay, line: Payline) {
    let paylineDisplay = '';
    const filtered = pay.Paylines.filter(x => x.OkToPay == true);
    if (this.changeRequests?.some(x => x.PersonId == pay.PersonId)) {
      //This EE has change requests.
      this.showRequestWarning = true;
      if (line.Errors == null) {
        line.Errors = [];
      }
      if (!line.Errors?.some(x => x.FieldName == 'changeRequest')) {
        this.changeRequests
          .filter(x => x.PersonId == pay.PersonId && x.RequestType == 'Address Change')
          .forEach(request => {
            line.Errors.push({
              FieldName: 'changeRequest',
              Message: `This person has a pending address change request`,
              WarningLevel: WarningLevel.Warning
            });
          });
      }
    }
    if (filtered.length > 1) {
      const index = filtered.findIndex(x => x.PaylineId == line.PaylineId) + 1;
      paylineDisplay = `Payline ${index} of ${pay.Paylines.filter(x => x.OkToPay).length}`;
    }

    return new SelectableManualPayline(
      this.payrollMgmt.getEmployeeFullName(pay),
      pay.PersonId,
      line,
      paylineDisplay
    );
  }
  private _reportGenerate() {
    const args = {
      PaylineIds: this.selectedLines,
      PayDate: this.payroll.PayDate,
      SeqNo: this.payroll.SequenceNumber
    };
    //paydate is actually a string and not a date.
    const paydate: any = this.payroll.PayDate;

    const year = new Date(Date.parse(paydate));

    this.reportService
      .dynamicReport(args, 'application/pdf', this.clientCode, 'PayStubHistory')
      .subscribe((result: any) => {
        if (result == null) {
          this.modalService.showAlertModal(
            'The report parameters that you entered did not generate any results. Please try again with different parameters.',
            ModalStates.Error
          );
        } else {
          downloadFile(
            result,
            `Client Pay Stubs_${this.clientCode}_${year.getFullYear()}_PD${
              year.getMonth() + 1
            }${year.getDate()}${year.getFullYear()}.pdf`
          );
        }
        this.selectedLines = [];
        this._updateScreen();
      });
  }
  private _openCalcPayModal(personId: number, paylineId: number) {
    const pay = this.payroll.EmployeePays.find(x => x.PersonId == personId);
    this.selEmployeePay = JSON.parse(JSON.stringify(pay));

    this.paylineDetailArgs = new OpenPaylineDetailsArgs(
      null,
      paylineId,
      personId,
      null,
      true,
      false,
      false
    );

    this.showPaylineDetails = true;
  }

  private _searchPerson(query: string) {
    // showing the top 100 results here. if pagination is needed, update the logic here
    this.peopleService
      .searchPeople(this.clientCode, { query: query, perPage: 100 })
      .subscribe(results => {
        this.data = [];
        if (!results.Items || !results.Items.length) {
          return;
        }

        results.Items.forEach(item => {
          const middle = `${item.MiddleName?.trim() ? ` ${item.MiddleName.trim()} ` : ' '}`;
          const name = `${item.FirstName}${middle}${item.LastName}`;
          const status = `${this.employeeStatuses[item.CurrentStatus.StatusId].Value}`;
          const alreadyPaid = `${
            this.payroll?.EmployeePays?.find(
              x => x.PersonId == item.Id && x.Paylines.find(x => x.OkToPay)
            )
              ? '(Add Another Pay)'
              : ''
          }`;
          const statusDate = `${new Date(
            item.CurrentStatus.DateStatusChange
          ).toLocaleDateString()}`;

          this.data.push({
            Id: item.Id,
            Value: `${item.EmployeeId} - ${name} - ${status} since ${statusDate} ${alreadyPaid}`
          });
        });
      });
  }

  private _updateScreen() {
    // clear the search field
    this.searchValue = null;

    // update this.paylines
    this.manualPaylines = [];

    const sorted = this.payroll.EmployeePays.sort((a, b) => {
      return this.payrollMgmt
        .getEmployeeFullName(a)
        .localeCompare(this.payrollMgmt.getEmployeeFullName(b));
    });

    for (let pay of sorted) {
      for (let line of pay.Paylines) {
        if (!line.OkToPay) {
          continue;
        }

        this.manualPaylines.push(this._createManualPayline(pay, line));
      }
    }
    if (
      !this.manualPaylines?.some(x => x.Payline.Errors?.some(y => y.FieldName == 'changeRequest'))
    ) {
      this.showRequestWarning = false;
    }
  }

  protected readonly CheckboxSizes = CheckboxSizes;
}

export class ManualPayline {
  Name: string;
  Payline: Payline;
  PersonId: number;
  MultiPaylineDisplay: string;

  constructor(
    name: string,
    personId: number,
    payline: Payline,
    multiPaylineDisplay: string = null
  ) {
    this.Name = name;
    this.PersonId = personId;
    this.Payline = payline;
    this.MultiPaylineDisplay = multiPaylineDisplay;
  }
}

export class SelectableManualPayline extends ManualPayline {
  Selected: boolean;
  constructor(
    name: string,
    personId: number,
    payline: Payline,
    multiPaylineDisplay: string = null
  ) {
    super(name, personId, payline, multiPaylineDisplay);
    this.Selected = false;
  }
}
